import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, ShieldAlert, ExternalLink, RefreshCw, Info } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Settings → Parental Consent transparency row.
 *
 * Read-only view of the umbrella COPPA parental-consent state for the
 * signed-in caregiver. Sources of truth:
 *   - GET /api/consent/parental/status   (presence, method, accepted_at)
 *
 * Friendly translation of the four method values (per Praxis option b):
 * plain English in the visible label, raw backend identifier exposed via
 * the `title` tooltip so support / debugging never have to ask the parent
 * to remember the technical method name.
 */
const METHOD_TRANSLATIONS = {
  stripe_card: {
    label: "Verified during paid checkout",
    blurb: "Your card issuer's identity check satisfies the COPPA requirement.",
  },
  email_loop_kba: {
    label: "Verified via email and identity questions",
    blurb: "You clicked our verification email and confirmed your identity.",
  },
  admin_override: {
    label: "Verified by your sponsoring clinician",
    blurb: "Your sponsoring doctor or speech-language pathologist attested on your behalf.",
  },
  legacy_pre_gate: {
    label: "Grandfathered (account pre-dates this requirement)",
    blurb: (
      "Your account was created before we added this verification step. " +
      "You're grandfathered, but we recommend completing the live verification " +
      "when you have a moment."
    ),
  },
};

export default function SettingsParentalConsentSection() {
  const [snap, setSnap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/consent/parental/status");
      setSnap(data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Failed to load consent status");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const hasConsent = !!snap?.has_consent;
  const method = snap?.method;
  const translation = method ? METHOD_TRANSLATIONS[method] : null;
  const isLegacy = method === "legacy_pre_gate";

  return (
    <section
      className="brutal-card bg-white p-5 mb-5"
      data-testid="settings-parental-consent-section"
    >
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="display text-xl font-extrabold flex items-center gap-2">
            <ShieldCheck size={20} className="text-[color:var(--coral)]" />
            Parental consent
          </h2>
          <p className="text-xs text-[color:var(--ink-soft)] mt-1 max-w-xl leading-snug">
            One-time verifiable consent recorded under COPPA. Required before
            any child profile can be created on your account.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          className="pill-btn"
          disabled={loading}
          aria-label="Reload consent status"
          data-testid="settings-parental-consent-reload"
        >
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {error && (
        <div className="brutal-card bg-[#FFE3E0] text-[color:var(--coral-deep)] p-3 mb-3 text-sm" data-testid="settings-parental-consent-error">
          {error}
        </div>
      )}

      {snap && (
        <>
          <StatusRow hasConsent={hasConsent} translation={translation} method={method} acceptedAt={snap.accepted_at} />
          {isLegacy && <LegacyPrompt />}
          {!hasConsent && <MissingPrompt />}
          <FooterLinks />
        </>
      )}
    </section>
  );
}

function StatusRow({ hasConsent, translation, method, acceptedAt }) {
  if (!hasConsent) {
    return (
      <div
        className="brutal-card bg-[#FFF3D6] p-4 mb-3 flex items-start gap-3"
        data-testid="settings-parental-consent-row"
        data-state="missing"
      >
        <ShieldAlert size={20} className="text-[color:var(--coral-deep)] mt-0.5 shrink-0" />
        <div>
          <div className="font-extrabold display text-base mb-0.5">Not on file</div>
          <div className="text-sm text-[color:var(--ink-soft)] leading-snug">
            You haven't completed parental consent yet. You'll be prompted the
            first time you try to create a child profile — or you can start it
            now using the button below.
          </div>
        </div>
      </div>
    );
  }
  const acceptedDate = acceptedAt ? new Date(acceptedAt).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : null;
  return (
    <div
      className="brutal-card bg-[#DCF2E0] p-4 mb-3"
      data-testid="settings-parental-consent-row"
      data-state="on-file"
    >
      <div className="flex items-start gap-3">
        <ShieldCheck size={20} className="text-[color:var(--brand-green,#3FA34D)] mt-0.5 shrink-0" />
        <div className="flex-1">
          <div className="font-extrabold display text-base mb-0.5">On file</div>
          <div
            className="text-sm leading-snug inline-flex items-center gap-1.5"
            data-testid="settings-parental-consent-method"
            title={method ? `Method (raw): ${method}` : ""}
          >
            {translation?.label || "Verified"}
            <Info size={12} className="text-[color:var(--ink-soft)] opacity-60" aria-hidden />
          </div>
          {translation?.blurb && (
            <div className="text-xs text-[color:var(--ink-soft)] mt-1 leading-snug">
              {translation.blurb}
            </div>
          )}
          {acceptedDate && (
            <div className="text-[11px] text-[color:var(--ink-soft)] mt-2" data-testid="settings-parental-consent-date">
              Recorded {acceptedDate}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function LegacyPrompt() {
  return (
    <div className="brutal-card bg-[color:var(--paper-muted,#F2EFE7)] p-3 mb-3 text-xs leading-snug" data-testid="settings-parental-consent-legacy-prompt">
      <strong>Want to complete live verification?</strong> Create a new child
      profile from the Profiles page and you'll be guided through the 90-second
      flow. Your existing profiles aren't affected either way.
    </div>
  );
}

function MissingPrompt() {
  return (
    <div className="text-xs leading-snug mb-3" data-testid="settings-parental-consent-missing-prompt">
      <Link to="/profiles" className="pill-btn primary text-xs inline-flex" data-testid="settings-parental-consent-start">
        Start verification →
      </Link>
    </div>
  );
}

function FooterLinks() {
  return (
    <div className="flex flex-wrap gap-3 text-xs text-[color:var(--ink-soft)] pt-2 border-t border-[color:var(--paper-muted,#E5E1D8)]">
      <Link to="/privacy" className="underline inline-flex items-center gap-1" data-testid="settings-parental-consent-privacy-link">
        Privacy policy <ExternalLink size={10} />
      </Link>
      <Link to="/demo-safety" className="underline inline-flex items-center gap-1" data-testid="settings-parental-consent-safety-link">
        Safety notice <ExternalLink size={10} />
      </Link>
    </div>
  );
}
